import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import { 
  Lightning, Users, Shield, Star, CheckCircle, ArrowRight, 
  Clock, Headphones, Trophy, Sparkle, Heart, Rocket
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const ModernWhyChooseUs = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate cards
      gsap.fromTo(cardsRef.current.children,
        {
          y: 60,
          opacity: 0,
          scale: 0.9
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.8,
          ease: "back.out(1.7)",
          stagger: 0.15,
          scrollTrigger: {
            trigger: cardsRef.current,
            start: "top 80%",
          }
        }
      );

      // Animate stats counters
      gsap.fromTo(statsRef.current.children,
        {
          scale: 0,
          opacity: 0
        },
        {
          scale: 1,
          opacity: 1,
          duration: 0.6,
          ease: "back.out(1.7)",
          stagger: 0.1,
          scrollTrigger: {
            trigger: statsRef.current,
            start: "top 85%",
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const features = [
    {
      icon: <Lightning weight="duotone" className="w-8 h-8" />,
      title: t('modernWhyChooseUs.features.speed.title'),
      description: t('modernWhyChooseUs.features.speed.description'),
      color: 'blue',
      gradient: 'from-blue-400 to-blue-500'
    },
    {
      icon: <Users weight="duotone" className="w-8 h-8" />,
      title: t('modernWhyChooseUs.features.personal.title'),
      description: t('modernWhyChooseUs.features.personal.description'),
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Shield weight="duotone" className="w-8 h-8" />,
      title: t('modernWhyChooseUs.features.quality.title'),
      description: t('modernWhyChooseUs.features.quality.description'),
      color: 'blue',
      gradient: 'from-blue-600 to-blue-700'
    },
    {
      icon: <Headphones weight="duotone" className="w-8 h-8" />,
      title: t('modernWhyChooseUs.features.support.title'),
      description: t('modernWhyChooseUs.features.support.description'),
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600'
    }
  ];

  const stats = [
    {
      icon: <Trophy weight="duotone" className="w-8 h-8 text-blue-400" />,
      number: "50+",
      label: t('modernWhyChooseUs.stats.projects')
    },
    {
      icon: <Star weight="duotone" className="w-8 h-8 text-blue-400" />,
      number: "5.0",
      label: t('modernWhyChooseUs.stats.rating')
    },
    {
      icon: <Clock weight="duotone" className="w-8 h-8 text-blue-400" />,
      number: "99.9%",
      label: t('modernWhyChooseUs.stats.uptime')
    },
    {
      icon: <Heart weight="duotone" className="w-8 h-8 text-blue-400" />,
      number: "100%",
      label: t('modernWhyChooseUs.stats.satisfaction')
    }
  ];

  return (
    <section ref={sectionRef} className="py-32 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-gradient-to-br from-blue-300/20 to-blue-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-blue-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-blue-100/10 to-blue-200/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-full border border-blue-200/50 mb-6">
            <Sparkle weight="duotone" className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">{t('modernWhyChooseUs.badge')}</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            {t('modernWhyChooseUs.title.part1')}{' '}
            <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              {t('modernWhyChooseUs.title.part2')}
            </span>
          </h2>
          
          <p className="text-xl text-slate-600 leading-relaxed">
            {t('modernWhyChooseUs.subtitle')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              whileHover={{ y: -5, scale: 1.02 }}
              className="group relative bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-xl text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                {feature.title}
              </h3>
              
              <p className="text-slate-600 leading-relaxed">
                {feature.description}
              </p>
              
              {/* Hover effect background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`} />
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 rounded-3xl p-12 mb-16 relative overflow-hidden"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:20px_20px]" />
          
          <div className="relative z-10">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('modernWhyChooseUs.statsTitle')}
              </h3>
              <p className="text-blue-200 text-lg">
                {t('modernWhyChooseUs.statsSubtitle')}
              </p>
            </div>
            
            <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={stat.label} className="text-center group">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl mb-4 group-hover:bg-white/20 transition-colors duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-blue-200 text-sm">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ModernWhyChooseUs;
